import Cookie from 'js-cookie'

import env from '@/constants/env'

import { updateCartAttributesReconcile } from '@/clients/shopify/client'
import type { Cart } from '@/types/shopify'
import { getCartAttributeByKey } from '../cart/utilities'

export const initGA4Attributes = async (cart?: Cart) => {
  if (!cart?.id) return

  let cartAttributesToUpdate = []

  const clientIdAttributeToUpdate = getClientIdCartAttributeToUpdate(cart)
  if (clientIdAttributeToUpdate) {
    cartAttributesToUpdate.push(clientIdAttributeToUpdate)
  }

  const eventParamsAttributeToUpdate = getEventParamsCartAttributeToUpdate(cart)
  if (eventParamsAttributeToUpdate) {
    cartAttributesToUpdate.push(eventParamsAttributeToUpdate)
  }

  if (cartAttributesToUpdate.length) {
    await updateCartAttributesReconcile(cart.id, [
      ...cart.attributes,
      ...cartAttributesToUpdate,
    ])
  }
}

const getClientIdCartAttributeToUpdate = (cart: Cart) => {
  const clientId = getGA4ClientId()
  const clientIdFromCart = getCartAttributeByKey(cart, 'ga4_client_id')

  if (clientId && clientIdFromCart !== clientId) {
    return {
      key: 'ga4_client_id',
      value: clientId,
    }
  }

  return null
}

const getEventParamsCartAttributeToUpdate = (cart: Cart) => {
  const eventParams = getGA4EventParams()

  const eventParamsStringFromCart = getCartAttributeByKey(
    cart,
    'ga4_event_params',
  )
  const eventParamsFromCart =
    eventParamsStringFromCart && JSON.parse(eventParamsStringFromCart)

  if (
    Object.keys(eventParams).length &&
    JSON.stringify(eventParams) !== JSON.stringify(eventParamsFromCart)
  ) {
    const eventParamsToSet = {
      ...eventParamsFromCart,
      ...eventParams,
    }
    return {
      key: 'ga4_event_params',
      value: JSON.stringify(eventParamsToSet),
    }
  }

  return null
}

const getGA4ClientId = (): string | undefined => {
  const gaCookie = Cookie.get('_ga')
  return gaCookie?.split('.').slice(-2).join('.')
}

const getGA4SessionId = (): number | undefined => {
  const ga4ID = env.PUBLIC_GA4_MEASUREMENT_ID?.split('-').pop()
  const gaSessionCookie = Cookie.get(`_ga_${ga4ID}`)
  const sessionId = gaSessionCookie?.split('.')[2]
  return sessionId ? parseInt(sessionId, 10) : undefined
}

const getGA4EventParams = () => {
  const sessionId = getGA4SessionId()
  const debugMode = env.PUBLIC_ENABLE_SERVER_SIDE_GA4_DEBUG_MODE === 'true'

  return {
    session_id: sessionId,
    debug_mode: debugMode,
  }
}
